<template>
  <v-navigation-drawer
    v-if="user"
    :value="showDrawer"
    @input="$emit('stateChanged', $event)"
    app
  >
    <v-sheet
      :color="$vuetify.theme.dark ? 'secondary' : 'accent'"
      dark
      elevation="4"
      class="d-flex align-center justify-center side-header"
    >
      <img src="/logo-small.png" class="side-logo" />
    </v-sheet>

    <!-- Nom utilisateur relocalisé : noir sur fond blanc, au-dessus du menu -->
    <v-list dense>
      <v-list-item :to="{ name: 'Profile' }" color="accent">
        <v-list-item-icon>
          <v-icon>mdi-account-circle-outline</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>{{ user.displayedName }}</v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-list>

    <v-divider></v-divider>

    <template v-if="user.clients && user.clients.length > 0">
      <v-sheet class="pa-2">
        <MyClientSwitch></MyClientSwitch>
      </v-sheet>
      <v-divider></v-divider>
    </template>

    <v-list dense>
      <v-list-item
        link
        color="accent"
        v-for="item of grantedMenuItems"
        :key="item.title"
        :to="{ name: item.routeName }"
      >
        <v-list-item-icon>
          <v-icon>{{ item.icon }}</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>{{ item.title }}</v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <!-- Personal views -->
      <template v-if="user.clients && user.clients.length > 0">
        <v-divider></v-divider>
        <v-list-item
          v-for="item of personalViewItems"
          :key="item.title"
          link
          color="accent"
          :to="{ name: item.routeName }"
        >
          <v-list-item-icon>
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-divider class="mt-4"></v-divider>
      </template>

      <!-- Admin -->
      <template v-if="grantedAdminItems.length > 0">
        <v-divider></v-divider>
        <v-subheader>Admin</v-subheader>
        <v-list-item
          v-for="item of grantedAdminItems"
          :key="item.title"
          link
          color="accent"
          :to="{ name: item.routeName }"
        >
          <v-list-item-icon>
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-divider class="mt-4"></v-divider>
      </template>

      <!-- Logout -->
      <v-list-item @click="$emit('logout')" link>
        <v-list-item-icon>
          <v-icon>mdi-logout</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>{{ $t("menu.logout") }}</v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-list>
  </v-navigation-drawer>
</template>

<script lang="ts">
import { UserWithRightOutDTO } from "@/api/models/user/user-with-right.out";
import { StoreGetter } from "@/store/storeGetter.decorator";
import Vue from "vue";
import Component from "vue-class-component";
import { ACLRight } from "@/api/acl-right.enum";
import MyClientSwitch from "./MyClientSwitch.vue";

const SiteMenuProps = Vue.extend({
  props: {
    showDrawer: {
      type: Boolean,
      default: true,
    },
  },
});

interface MenuItem {
  icon: string;
  title: string;
  routeName: string;
  right?: ACLRight | ACLRight[];
}

@Component({
  components: {
    MyClientSwitch,
  },
})
export default class SiteMenu extends SiteMenuProps {
  @StoreGetter("user/user") user!: UserWithRightOutDTO;

  public get menuItems(): MenuItem[] {
    const items = [
      {
        icon: "mdi-home-outline",
        title: this.$tc("menu.home"),
        routeName: "Home",
      },
      {
        icon: "mdi-account-group-outline",
        title: this.$tc("menu.clients"),
        routeName: "Clients",
        right: ACLRight.CLIENT_LIST,
      },
      {
        icon: "mdi-store-outline",
        title: this.$tc("menu.shops"),
        routeName: "Shops",
        right: ACLRight.SHOP_LIST,
      },
      {
        icon: "mdi-map-outline",
        title: this.$tc("menu.map"),
        routeName: "ShopsMap",
        right: ACLRight.SHOP_LIST,
      },
      {
        icon: "mdi-office-building-outline",
        title: this.$tc("menu.enterprises"),
        routeName: "Enterprises",
        right: ACLRight.ENTERPRISE_LIST,
      },
      {
        icon: "mdi-contacts-outline",
        title: this.$tc("menu.persons"),
        routeName: "Persons",
        right: ACLRight.PERSON_LIST,
      },
      {
        icon: "mdi-wrench-clock-outline",
        title: this.$tc("menu.interventions"),
        routeName: "Interventions",
        right: this.user.hasRight(ACLRight.INSTALLATION_ADMIN_LIST_INTERVENTION)
          ? ACLRight.INSTALLATION_ADMIN_LIST_INTERVENTION
          : ACLRight.INSTALLATION_ADMIN_LIST_MY_INTERVENTION,
      },
    ];

    // Call-center
    if (!this.user.hasRight(ACLRight.CALL_CENTER_LIST)) {
      items.push({
        icon: "mdi-headset",
        title: this.$tc("menu.callCenter"),
        routeName: "CallCenterTicketCreate",
      });
    } else {
      items.push({
        icon: "mdi-headset",
        title: this.$tc("menu.callCenter"),
        routeName: "CallCenterTickets",
        right: this.user.hasRight(ACLRight.CALL_CENTER_LIST)
          ? ACLRight.CALL_CENTER_LIST
          : ACLRight.CALL_CENTER_LIST_MY_TICKET,
      });
    }

    return items;
  }

  public get adminItems(): MenuItem[] {
    return [
      {
        icon: "mdi-badge-account-outline",
        title: this.$tc("menu.users"),
        routeName: "Users",
        right: ACLRight.USER_LIST,
      },
      {
        icon: "mdi-account-key-outline",
        title: this.$tc("menu.roles"),
        routeName: "Roles",
        right: [ACLRight.ROLE_LIST, ACLRight.ACL_LIST],
      },
      {
        icon: "mdi-file-document-edit-outline",
        title: this.$tc("menu.templates"),
        routeName: "Templates",
        right: ACLRight.CONTRACT_TEMPLATE_LIST,
      },
      {
        icon: "mdi-chart-box-outline",
        title: this.$tc("menu.statistics"),
        routeName: "DailyStatistics",
        right: [ACLRight.EXTERNAL_FINANCIAL_DATA_GET_DAILY_STATISTICS],
      },
      {
        icon: "mdi-account-cash-outline",
        title: this.$tc("menu.agencyManagerPayment"),
        routeName: "AgencyManagerPayment",
        right: [ACLRight.EXTERNAL_FINANCIAL_DATA_GET_STATISTICS_FOR_ALL_CLIENT],
      },
      {
        icon: "mdi-medal-outline",
        title: this.$tc("menu.players1000"),
        routeName: "Players1000",
        right: [ACLRight.PLAYERS_1000],
      },
      {
        icon: "mdi-trophy-outline",
        title: this.$tc("menu.players2000"),
        routeName: "Players2000",
        right: [ACLRight.CALL_CENTER_LIST],
      },
      {
        icon: "mdi-cog-outline",
        title: this.$tc("menu.settings"),
        routeName: "Settings",
        right: [ACLRight.DOCUSIGN_HAS_CONSENT],
      },
    ];
  }

  public get personalViewItems(): MenuItem[] {
    return [
      {
        icon: "mdi-store-outline",
        title: this.$tc("menu.myShop"),
        routeName: "MyShop",
      },
      {
        icon: "mdi-ticket-outline",
        title: this.$tc("menu.myTickets"),
        routeName: "MyCallCenterTicket",
      },
    ];
  }

  public get grantedAdminItems() {
    return this.adminItems.filter((item) => this.user.hasRight(item.right));
  }

  public get grantedMenuItems() {
    return this.menuItems.filter((item) => this.user.hasRight(item.right));
  }
}
</script>
<style lang="scss" scoped>
.side-header {
  height: 64px; // identique à la hauteur par défaut de v-app-bar
  // z-index pour que l'ombre portée (elevation) passe au-dessus du contenu
  // qui suit, comme l'app bar
  position: relative;
  z-index: 1;
}
.side-logo {
  max-height: 32px;
  display: block; // centrage géré par flex sur .side-header
}
</style>
